import React, { useState, useRef, useEffect } from 'react';
import { Plus, Copy, Trash2, GripHorizontal, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { Button } from './ui/button';
import Canvas from './Canvas';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from './ui/context-menu';

const FlowCanvas = ({
    screens,
    activeScreenIndex,
    onSelectScreen,
    onDeleteScreen,
    onDuplicateScreen,
    onReorderScreens,
    onAddScreen,
    onEditScreen,
    globalSettings
}) => {
    const [draggedIndex, setDraggedIndex] = useState(null);
    const dragOverIndex = useRef(null);

    // Pan and Zoom state
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    // Handle Panning
    const handleMouseDown = (e) => {
        // Only pan if middle mouse button or spacebar is held, or if clicking on the background
        if (e.button === 1 || e.target === containerRef.current || e.target.classList.contains('pan-area')) {
            setIsPanning(true);
            setLastMousePos({ x: e.clientX, y: e.clientY });
            e.preventDefault(); // Prevent text selection
        }
    };

    const handleMouseMove = (e) => {
        if (isPanning) {
            const deltaX = e.clientX - lastMousePos.x;
            const deltaY = e.clientY - lastMousePos.y;
            setPosition(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
            setLastMousePos({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        setIsPanning(false);
    };

    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            const newScale = Math.min(Math.max(0.1, scale * delta), 3);
            setScale(newScale);
        } else {
            // Optional: Pan with scroll if not zooming
            // setPosition(prev => ({ x: prev.x - e.deltaX, y: prev.y - e.deltaY }));
        }
    };

    // Attach global mouse up to handle dragging outside container
    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [isPanning, lastMousePos]);


    // Drag and Drop for Reordering
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index); // Required for Firefox
        // Create a ghost image if needed
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        dragOverIndex.current = index;
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null) return;

        if (draggedIndex !== index) {
            onReorderScreens(draggedIndex, index);
        }
        setDraggedIndex(null);
        dragOverIndex.current = null;
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        dragOverIndex.current = null;
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Duplicate: Ctrl+D or Cmd+D
            if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
                e.preventDefault();
                if (activeScreenIndex !== null && activeScreenIndex >= 0 && activeScreenIndex < screens.length) {
                    onDuplicateScreen(activeScreenIndex);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeScreenIndex, screens.length, onDuplicateScreen]);

    return (
        <div
            className="flex-1 relative overflow-hidden bg-muted/30 cursor-grab active:cursor-grabbing"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onWheel={handleWheel}
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
        >
            {/* Controls */}
            <div className="absolute bottom-4 right-4 z-50 flex gap-2 bg-card p-2 rounded-lg shadow-md border border-border">
                <Button variant="ghost" size="icon" onClick={() => setScale(s => Math.max(0.1, s - 0.1))}>
                    <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="flex items-center text-xs font-mono w-12 justify-center">
                    {Math.round(scale * 100)}%
                </span>
                <Button variant="ghost" size="icon" onClick={() => setScale(s => Math.min(3, s + 0.1))}>
                    <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setScale(1); setPosition({ x: 0, y: 0 }); }}>
                    <Move className="h-4 w-4" />
                </Button>
            </div>

            {/* Pannable Content */}
            <div
                className="pan-area absolute top-1/2 left-10 flex items-start gap-8 transition-transform duration-75 ease-out origin-left"
                style={{
                    transform: `translate(${position.x}px, ${position.y - 300}px) scale(${scale})`, // Center vertically approx
                }}
            >
                {screens.map((screen, index) => (
                    <ContextMenu key={screen.id}>
                        <ContextMenuTrigger>
                            <div
                                className={`
                                    group relative flex flex-col gap-2 transition-all duration-200
                                    ${draggedIndex === index ? 'opacity-50 scale-95' : 'opacity-100'}
                                `}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent panning start
                                    onSelectScreen(index);
                                }}
                                onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    onSelectScreen(index);
                                    if (onEditScreen) onEditScreen();
                                }}
                                onMouseDown={(e) => e.stopPropagation()} // Prevent panning when clicking card
                            >
                                {/* Header */}
                                <div className={`
                                    flex items-center justify-between px-3 py-2 rounded-t-lg bg-card border-x border-t
                                    ${activeScreenIndex === index ? 'border-primary' : 'border-border'}
                                `}>
                                    <div className="flex items-center gap-2">
                                        <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                                            <GripHorizontal className="h-4 w-4" />
                                        </div>
                                        <span className="text-sm font-medium truncate max-w-[120px]">
                                            {screen.name || `Screen ${index + 1}`}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDuplicateScreen(index);
                                            }}
                                            title="Duplicate"
                                        >
                                            <Copy className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-destructive hover:text-destructive"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteScreen(index);
                                            }}
                                            title="Delete"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Screen Preview */}
                                <div className={`
                                    relative overflow-hidden bg-white rounded-b-lg shadow-sm border
                                    ${activeScreenIndex === index ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
                                `}
                                    style={{
                                        width: '240px',
                                        height: '520px',
                                    }}
                                >
                                    {/* Overlay to capture clicks but allow selection */}
                                    <div className="absolute inset-0 z-10 cursor-pointer" />

                                    {/* Scaled Canvas */}
                                    <div style={{ transform: 'scale(0.64)', transformOrigin: 'top left', width: '375px', height: '812px' }}>
                                        <Canvas
                                            screen={screen}
                                            selectedElement={null}
                                            onSelectElement={() => { }}
                                            isPreview={true}
                                            globalSettings={globalSettings}
                                        />
                                    </div>
                                </div>

                                {/* Footer / Sequence Number */}
                                <div className="flex justify-center">
                                    <div className="bg-muted text-muted-foreground text-xs font-mono px-2 py-1 rounded-full">
                                        {index + 1}
                                    </div>
                                </div>
                            </div>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                            <ContextMenuItem onClick={() => {
                                onSelectScreen(index);
                                if (onEditScreen) onEditScreen();
                            }}>
                                Edit Screen
                            </ContextMenuItem>
                            <ContextMenuSeparator />
                            <ContextMenuItem onClick={() => onDuplicateScreen(index)}>
                                Duplicate
                            </ContextMenuItem>
                            <ContextMenuItem
                                className="text-destructive"
                                onClick={() => onDeleteScreen(index)}
                            >
                                Delete
                            </ContextMenuItem>
                        </ContextMenuContent>
                    </ContextMenu>
                ))}

                {/* Add New Screen Button */}
                <div className="flex items-center justify-center h-[520px] w-[100px]">
                    <Button
                        variant="outline"
                        className="h-12 w-12 rounded-full border-dashed border-2 hover:border-primary hover:text-primary transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onAddScreen) onAddScreen();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Plus className="h-6 w-6" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FlowCanvas;
